import { useState, useEffect, useCallback } from 'react';

const STORAGE_KEY_PREFIX = 'form-preview-override-';

interface UseFormPreviewResult {
  activeDefinition: PolicyFormDefinition | undefined;
  hasOverride: boolean;
  setOverrideDefinition: (definition: PolicyFormDefinition | undefined) => void;
  clearOverride: () => void;
}

export function useFormPreview(
  formId: string,
  originalDefinition?: PolicyFormDefinition,
): UseFormPreviewResult {
  const [overrideDefinition, setOverrideDefinition] = useState<PolicyFormDefinition | undefined>();

  // Load override from localStorage on mount
  useEffect(() => {
    if (!originalDefinition) return;

    const storageKey = `${STORAGE_KEY_PREFIX}${formId}`;

    // Check localStorage
    const stored = localStorage.getItem(storageKey);
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        setOverrideDefinition(parsed);
      } catch (e) {
        console.error('Failed to parse stored override:', e);
      }
    }
  }, [formId, originalDefinition]);

  const handleSetOverride = useCallback(
    (definition: PolicyFormDefinition | undefined) => {
      const storageKey = `${STORAGE_KEY_PREFIX}${formId}`;

      if (definition) {
        // Store in localStorage
        localStorage.setItem(storageKey, JSON.stringify(definition));
      } else {
        // Remove from localStorage when definition is undefined
        localStorage.removeItem(storageKey);
      }

      setOverrideDefinition(definition);
    },
    [formId],
  );

  const clearOverride = useCallback(() => {
    const storageKey = `${STORAGE_KEY_PREFIX}${formId}`;
    localStorage.removeItem(storageKey);
    setOverrideDefinition(undefined);
  }, [formId]);

  return {
    activeDefinition: overrideDefinition || originalDefinition,
    hasOverride: !!overrideDefinition,
    setOverrideDefinition: handleSetOverride,
    clearOverride,
  };
}
